/**
 * 料金モジュール — 統一料金JSON(butler-pricing-unified.json)の単一読み取り口。
 *
 * 背景(2026-07-21 Yuichi指令「各サイトもjson読み取りに修正しろ。金額表示が想定通りであること」):
 *   税込額をハードコードで書かず、JSONの税別真値(amount_ex_tax)から計算式で税込を算出する。
 *   税率(taxRate)・丸め規則(rounding_rule)はJSON tax_policy 1箇所のみが真値(各ページで再定義しない)。
 *
 * 正典: src/data/butler-pricing-unified.json(全サイト共通・sha256一致)。
 * 丸め: round_half_to_even_to_integer_yen(銀行家の丸め・.5は偶数側)。
 *       JSON round_trip_verification 14件を1円の狂いなく再現する(FP誤差はcent換算で除去)。
 * site-thebutler / site-japanbutlerassociation と同一機構(同一正本JSON由来)。
 */
import unified from '../data/butler-pricing-unified.json';

/** JSON tax_policy の税率(唯一の真値)。各ページ・コンポーネントで再定義しない。 */
export const TAX_RATE: number = unified.tax_policy.taxRate;

/**
 * 税別額 → 税込額(整数円)。round_half_to_even(JSON rounding_rule)。
 * FP誤差を避けるためcent(×100)換算で丸め判定する。
 */
export function incTax(exAmount: number): number {
  const cents = Math.round(exAmount * (1 + TAX_RATE) * 100);
  const whole = Math.floor(cents / 100);
  const rem = cents - whole * 100; // 0..99
  if (rem < 50) return whole;
  if (rem > 50) return whole + 1;
  // ちょうど .50 → 偶数側へ(banker's rounding)
  return whole % 2 === 0 ? whole : whole + 1;
}

/** 整数円 → 3桁区切り文字列(例: 780000 → "780,000")。 */
export function yen(amount: number): string {
  return amount.toLocaleString('en-US');
}

/** 税別額 → 税込表示文字列(例: 709091 → "780,000")。 */
export function incYen(exAmount: number): string {
  return yen(incTax(exAmount));
}

// --- プラン参照(JSON経由) -------------------------------------------------

interface PlanRef {
  id: string;
  ex: number;
  inc: number;
  incYen: string;
  name?: string;
}

/** JSONを再帰走査し、id + amount_ex_tax を持つ全entryを平坦化した参照表。 */
const flat: Record<string, PlanRef> = {};

function register(id: string, ex: number, name?: string): void {
  if (typeof ex === 'number' && !(id in flat)) {
    flat[id] = { id, ex, inc: incTax(ex), incYen: incYen(ex), name };
  }
}

function walk(node: unknown): void {
  if (!node || typeof node !== 'object') return;
  const obj = node as Record<string, unknown>;
  if (typeof obj.id === 'string' && typeof obj.amount_ex_tax === 'number') {
    register(obj.id, obj.amount_ex_tax, obj.label as string | undefined);
  }
  for (const key of Object.keys(obj)) walk(obj[key]);
}
walk(unified);

/** id でプランを引く。未登録なら undefined。 */
export function getPlan(id: string): PlanRef | undefined {
  return flat[id];
}

/** id の税込表示文字列(例: "ac-essential" → "780,000")。未登録はビルド時throw。 */
export function incYenOf(id: string): string {
  const p = flat[id];
  if (!p) throw new Error(`pricing: unknown plan id "${id}"`);
  return p.incYen;
}

/** id の税込額(整数円・数値)。未登録は throw。例: "ac-essential" → 780000。 */
export function incOf(id: string): number {
  const p = flat[id];
  if (!p) throw new Error(`pricing: unknown plan id "${id}"`);
  return p.inc;
}

/** id の税別真値(amount_ex_tax)。未登録は throw。 */
export function exOf(id: string): number {
  const p = flat[id];
  if (!p) throw new Error(`pricing: unknown plan id "${id}"`);
  return p.ex;
}
